import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Printer, Mail, ArrowLeft, PackageCheck, Trash2 } from "lucide-react";
import { inr, ymd } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { QRCodeSVG } from "qrcode.react";

export default function DocumentView() {
  const { doc_id } = useParams();
  const nav = useNavigate();
  const { company } = useAuth();
  const [doc, setDoc] = useState(null);

  const load = () => api.get(`/documents/${doc_id}`).then(({ data }) => setDoc(data));
  useEffect(() => { load(); }, [doc_id]);

  if (!doc) return <div className="p-8 text-slate-500">Loading…</div>;

  const kindLabel = doc.kind === "INV" ? "Tax Invoice" : "Purchase Order";
  const partyLabel = doc.kind === "INV" ? "Bill To" : "Supplier";

  const sendEmail = async () => {
    try {
      await api.post(`/documents/${doc_id}/send-email`);
      toast.success("Email sent successfully");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    }
  };

  const receiveStock = async () => {
    if (!window.confirm("Mark stock as received against this PO? Inventory will be updated.")) return;
    try {
      await api.post(`/documents/${doc_id}/receive-stock`);
      toast.success("Stock received and inventory updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${doc.number}? This will reverse related inventory and payments.`)) return;
    await api.delete(`/documents/${doc_id}`);
    toast.success("Deleted");
    nav(doc.kind === "INV" ? "/invoices" : "/purchase-orders");
  };

  return (
    <div>
      <div className="glass-header sticky top-0 z-10 px-8 py-4 flex items-center justify-between no-print">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <span className="font-display font-bold text-lg">{doc.number}</span>
          <Badge variant="outline" className="uppercase text-[10px] tracking-wider">{doc.status}</Badge>
        </div>
        <div className="flex gap-2">
          {doc.kind === "PO" && !doc.stock_received && (
            <Button variant="outline" onClick={receiveStock} data-testid="receive-stock-btn">
              <PackageCheck className="w-4 h-4 mr-1" /> Receive Stock
            </Button>
          )}
          <Button variant="outline" onClick={sendEmail} data-testid="email-doc-btn">
            <Mail className="w-4 h-4 mr-1" /> Email
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => window.print()} data-testid="print-doc-btn">
            <Printer className="w-4 h-4 mr-1" /> Print / PDF
          </Button>
          <Button variant="ghost" size="icon" onClick={del}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
        </div>
      </div>

      <div className="p-4 sm:p-8">
        <Card className="print-page max-w-4xl mx-auto p-6 sm:p-10 border-slate-200 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 sm:gap-6 pb-6 border-b border-slate-200">
            <div className="flex items-start gap-4 min-w-0">
              <img src="/logo.jpg" alt={company?.name || "The Smith International"} className="h-16 sm:h-20 w-auto object-contain flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="font-display text-xl sm:text-2xl font-black leading-tight break-words">{company?.name || "The Smith International"}</h1>
                {company?.address && (
                  <p className="text-xs text-slate-600 mt-1 max-w-xs whitespace-pre-line break-words">{company.address}</p>
                )}
                <div className="text-xs text-slate-500 mt-2 space-y-0.5 break-words">
                  {company?.phone && <div>Phone: {company.phone}</div>}
                  {company?.email && <div>Email: {company.email}</div>}
                  {company?.website && <div>Web: {company.website}</div>}
                  {company?.gstin && <div className="font-semibold text-slate-700 pt-0.5">GSTIN: {company.gstin}</div>}
                </div>
              </div>
            </div>
            <div className="sm:text-right shrink-0">
              <div className="font-display text-xl sm:text-2xl font-bold whitespace-nowrap">{kindLabel}</div>
              <div className="text-slate-500 mt-1 break-all">{doc.number}</div>
              <div className="text-slate-500 text-sm mt-3">Date: {ymd(doc.date)}</div>
              {doc.due_date && <div className="text-slate-500 text-sm">Due: {ymd(doc.due_date)}</div>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 mt-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{partyLabel}</div>
              <div className="font-semibold text-slate-900">{doc.party_name}</div>
              {doc.party_address && <div className="text-sm text-slate-600 whitespace-pre-line">{doc.party_address}</div>}
              {doc.party_state && <div className="text-sm text-slate-600">{doc.party_state}</div>}
              {doc.party_gstin && <div className="text-xs text-slate-500 mt-1">GSTIN: {doc.party_gstin}</div>}
            </div>
            <div>
              {doc.kind === "PO" && (doc.ship_to_option === "self" ? (
                <>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Ship To</div>
                  <div className="font-semibold text-slate-900">{company?.name || "The Smith International"}</div>
                  <div className="text-sm text-slate-600 whitespace-pre-line">{company?.address}</div>
                  {company?.phone && <div className="text-xs text-slate-500 mt-1">Phone: {company.phone}</div>}
                  {company?.gstin && <div className="text-xs text-slate-500">GSTIN: {company.gstin}</div>}
                </>
              ) : company?.po_ship_to?.name ? (
                <>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Ship To</div>
                  <div className="font-semibold text-slate-900">{company.po_ship_to.name}</div>
                  <div className="text-sm text-slate-600 whitespace-pre-line">{company.po_ship_to.address}</div>
                  {company.po_ship_to.gstin && <div className="text-xs text-slate-500 mt-1">GSTN: {company.po_ship_to.gstin}</div>}
                  {company.po_ship_to.contact && <div className="text-xs text-slate-500">Contact: {company.po_ship_to.contact}</div>}
                </>
              ) : null)}
              {doc.kind !== "PO" && (
                <div className="text-right">
                  {doc.reference && (
                    <>
                      <div className="text-xs uppercase tracking-wider text-slate-500">Reference</div>
                      <div className="text-sm text-slate-700">{doc.reference}</div>
                    </>
                  )}
                  <div className="text-xs uppercase tracking-wider text-slate-500 mt-3">Tax</div>
                  <div className="text-sm text-slate-700">{doc.interstate ? "IGST (Inter-state)" : "CGST + SGST"}</div>
                </div>
              )}
            </div>
          </div>

          {doc.kind === "PO" && (doc.quotation_ref || doc.quotation_date || doc.payment_terms || doc.shipping_terms) && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border border-slate-200 rounded-md p-4 bg-slate-50">
              <TermCell label="Quotation" value={doc.quotation_ref || "—"} />
              <TermCell label="Qtn Date" value={ymd(doc.quotation_date) || "—"} />
              <TermCell label="Payment Terms" value={doc.payment_terms || "—"} />
              <TermCell label="Shipping Terms" value={doc.shipping_terms || "—"} />
            </div>
          )}

          <table className="w-full mt-8 border-collapse">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-y border-slate-200">
                <th className="py-3">#</th>
                <th>Item</th>
                <th>HSN</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Rate</th>
                <th className="text-right">GST%</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {doc.items.map((l, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 text-slate-500">{i + 1}</td>
                  <td>{l.item_name}<div className="text-xs text-slate-500">{l.unit}</div></td>
                  <td className="text-slate-500">{l.hsn || "—"}</td>
                  <td className="text-right number-cell">{l.quantity}</td>
                  <td className="text-right number-cell">{inr(l.rate)}</td>
                  <td className="text-right number-cell">{l.gst_rate}%</td>
                  <td className="text-right number-cell font-semibold">{inr(l.quantity * l.rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end mt-6">
            <div className="w-full max-w-sm space-y-2 text-sm">
              <Row label="Subtotal" value={inr(doc.subtotal)} />
              {doc.interstate ? (
                <Row label="IGST" value={inr(doc.igst)} />
              ) : (
                <>
                  <Row label="CGST" value={inr(doc.cgst)} />
                  <Row label="SGST" value={inr(doc.sgst)} />
                </>
              )}
              <div className="flex justify-between font-display font-black text-lg pt-3 border-t border-slate-300">
                <span>Total</span><span className="number-cell">{inr(doc.total)}</span>
              </div>
              <Row label="Amount Paid" value={inr(doc.amount_paid || 0)} muted />
              <div className="flex justify-between font-semibold text-rose-600 pt-1 border-t border-slate-200">
                <span>Balance Due</span><span className="number-cell">{inr((doc.total || 0) - (doc.amount_paid || 0))}</span>
              </div>
            </div>
          </div>

          {doc.kind === "PO" && doc.special_instructions && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-semibold">
                Comments or Special Instructions
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                {doc.special_instructions}
              </div>
            </div>
          )}

          {doc.kind === "INV" && company?.bank?.account_number && (
            <BankBlock doc={doc} bank={company.bank} companyName={company.name} />
          )}

          {doc.notes && (
            <div className="mt-8 pt-4 border-t border-slate-200 text-sm text-slate-600">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Notes</div>
              {doc.notes}
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-semibold text-slate-700">{company?.name || "The Smith International"}</div>
                {company?.address && <div className="mt-0.5">{company.address}</div>}
                <div className="mt-0.5">
                  {company?.phone && <span>Ph: {company.phone}</span>}
                  {company?.email && <span> · {company.email}</span>}
                  {company?.website && <span> · {company.website}</span>}
                </div>
              </div>
              <Link to="/" className="hover:text-indigo-600 no-print">Back to dashboard</Link>
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-400">
              This is a computer-generated {kindLabel.toLowerCase()} and does not require a signature.
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className={`flex justify-between ${muted ? "text-slate-500" : ""}`}>
      <span>{label}</span><span className="number-cell">{value}</span>
    </div>
  );
}

function TermCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}

function BankBlock({ doc, bank, companyName }) {
  const balance = Math.max(0, (doc.total || 0) - (doc.amount_paid || 0));
  const vpa = bank.upi_vpa;
  let qrValue;
  if (vpa) {
    // Standard UPI intent
    const params = new URLSearchParams({
      pa: vpa,
      pn: bank.account_name || companyName || "Merchant",
      am: balance.toFixed(2),
      cu: "INR",
      tn: `Invoice ${doc.number}`,
    });
    qrValue = `upi://pay?${params.toString()}`;
  } else {
    // Fallback: plain-text bank details QR
    qrValue = [
      `Pay to: ${bank.account_name || companyName || ""}`,
      `A/C No: ${bank.account_number}`,
      `IFSC: ${bank.ifsc}`,
      `Bank: ${bank.bank_name || ""}`,
      `Amount: INR ${balance.toFixed(2)}`,
      `Ref: ${doc.number}`,
    ].join("\n");
  }
  return (
    <div className="mt-8 pt-4 border-t border-slate-200">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
        Payment Details
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6 items-start">
        <table className="text-sm">
          <tbody>
            <BankRow label="Beneficiary" value={bank.account_name} />
            <BankRow label="Bank" value={bank.bank_name} />
            <BankRow label="Account No" value={bank.account_number} mono />
            <BankRow label="IFSC" value={bank.ifsc} mono />
            {vpa && <BankRow label="UPI ID" value={vpa} mono />}
          </tbody>
        </table>
        <div className="flex flex-col items-center gap-1 print:block">
          <div className="bg-white p-2 border border-slate-200 rounded">
            <QRCodeSVG value={qrValue} size={112} level="M" includeMargin={false} />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            {vpa ? "Scan to pay via UPI" : "Scan for payment details"}
          </div>
        </div>
      </div>
    </div>
  );
}

function BankRow({ label, value, mono }) {
  if (!value) return null;
  return (
    <tr>
      <td className="py-1 pr-6 text-slate-500 text-xs uppercase tracking-wider">{label}</td>
      <td className={`py-1 text-slate-800 ${mono ? "font-mono" : "font-medium"}`}>{value}</td>
    </tr>
  );
}
